import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useSystemAlert } from "@src/api/alerts/use-system-alert";
import { useIsHeadless } from "@src/api/configurations/is-headless";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { useMissingShopifyScopes } from "@src/components/LimitAccess/hooks";

import {
  AlertSystemData,
  computeRawAlerts,
  filterSuppressedAlerts,
  sortAlertsByPriority,
} from "./types";

export function useAlertSystem() {
  const location = useLocation();
  const orgQuery = useGetOrg();
  const scriptStatusQuery = useGetScriptStatus(orgQuery.data?.org?.id);
  const isHeadless = useIsHeadless();
  const hasRequiredShopifyScopes = useMissingShopifyScopes().length === 0;
  const systemAlert = useSystemAlert();

  const alertData: AlertSystemData = useMemo(() => {
    const data = scriptStatusQuery.data;
    // expected/found only exist on certain script status types
    const expected = data && "expected" in data ? data.expected : undefined;
    const found = data && "found" in data ? data.found : undefined;
    const hasTemplateScript =
      data && "hasTemplateScript" in data ? data.hasTemplateScript : undefined;
    const hasProductThemeScript =
      data && "hasProductThemeScript" in data
        ? data.hasProductThemeScript
        : undefined;

    return {
      isInstalled: orgQuery.data?.org?.installed,
      scriptStatusType: data?.type,
      scriptStatusExpected: expected,
      scriptStatusFound: found,
      hasTemplateScript,
      hasProductThemeScript,
      hasRequiredShopifyScopes,
      isHeadless,
      pathname: location.pathname,
      hasSystemAlert: Boolean(systemAlert),
    };
  }, [
    orgQuery.data?.org?.installed,
    scriptStatusQuery.data,
    hasRequiredShopifyScopes,
    isHeadless,
    location.pathname,
    systemAlert,
  ]);

  const rawAlerts = useMemo(() => computeRawAlerts(alertData), [alertData]);

  const activeAlerts = useMemo(
    () => filterSuppressedAlerts(rawAlerts),
    [rawAlerts]
  );

  const sortedAlerts = useMemo(
    () => sortAlertsByPriority(activeAlerts),
    [activeAlerts]
  );

  return {
    alerts: sortedAlerts,
    alertData,
    orgQuery,
    scriptStatusQuery,
    isHeadless,
    hasRequiredShopifyScopes,
    systemAlert,
  };
}
